import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import {
  BodyLeftContainer,
  MainContainer,
} from '~/styles/BaseStyledComponents';
import { useHistory, useParams } from 'react-router-dom';
import {
  fetchSingleInvoice,
  getDebtorInvoices,
} from '~/store/actions/invoiceActions';
import { ApiStatus, Language, RootState } from '~/store/types/sharedTypes';
import SpinnerCircular from '~/components/shared/Spinner/SpinnerCircular';
import { theme } from '~/styles/themes';
import {
  FontWeights,
  Typography,
} from '~/components/shared/Typography/Typography';
import { MediaQueries } from '~/constants/constVars';
import useTranslate from '~/hooks/useTranslate';
import { InvoiceTabValue } from '~/Interfaces';
import { InvoiceStatuses } from '~/enums';
import { setLanguage } from '~/store/slices/intl.slice';
import { StyledIcon, SvgSrc } from '~/components/shared/Icon/Icon';
import { useWindowProperties } from '@qred/qit-ui';
import PaymentMethodsHeader from './components/PaymentMethods/PaymentMethodsHeader';
import SinglePaymentMethod from './components/PaymentMethods/SinglePaymentMethod';
import InvoicePageBody from './components/InvoicePageBody';
import InvoicePageHeader from './components/InvoicePageHeader';
import PayInInstalments from './components/PaymentMethods/PayInInstalments';
import NotConnected from './components/NotConnected';
import CustomerSupport from './components/CustomerSupport';

export const InvoiceLoaderHolder = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1 0;
  width: 100%;
`;

export const PaymentMethodBodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  align-self: center;
  padding: 0 2em;
  flex: 1 0;
  width: 100%;
  background-color: #f8f9f5;
  @media (orientation: landscape) {
    max-width: 40em;
  }
`;

export const PaymentMethodMainContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #f8f9f5;
`;

export const PaymentMethodHeaderContainer = styled.div<{
  withSubtext?: boolean;
}>`
  width: 100%;
  min-height: 18rem;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  flex-direction: column;
  @media ${MediaQueries.mediumScreen} {
    ${(props) =>
      !props.withSubtext &&
      css`
        margin-bottom: 40px;
      `}
  }
  @media ${MediaQueries.smallScreen} {
    min-height: 12rem;
    ${(props) =>
      !props.withSubtext &&
      css`
        margin-bottom: 20px;
      `}
  }
`;

export const SinglePaymentMethodContainer = styled.div`
  width: 100%;
  cursor: pointer;
`;

export const StepCircle = styled.div<{
  green?: boolean;
}>`
  height: 10px;
  width: 10px;
  background-color: ${(props) => (props.green ? '#2FD67A' : '#E9E9E9')};
  border-radius: 50%;
  display: inline-block;
  margin: 10px;
`;

export const Circle = styled.div`
  width: 60px
  height: 60px;
  border: 2px solid #2FD67A;
  border-radius: 50%;
  display: table;
  margin: auto;
  position: fixed;
  bottom: 15px;
  left: 15px;
  background: white;
  cursor: pointer;
`;

export const HeadsetIcon = styled.div`
  display: table-cell;
  vertical-align: middle;
  text-align: center;
  max-width: 27px;
  max-height: 27px;
`;

// Analytics
export enum PaymentMethodPageEnum {
  None = 0,
  PaymentMethods = 1,
  PayInInstalmentsPage = 2,
  DelayPayment = 3,
  PayOnInvoiceDueDate = 4,
  Support = 5,
}

const Invoice = () => {
  const history = useHistory();
  const params = useParams();
  const dispatch = useDispatch();
  const translate = useTranslate();

  const { isDesktop } = useWindowProperties();

  useEffect(() => {
    const { invoiceId } = params as any;
    dispatch(fetchSingleInvoice(invoiceId));
  }, [params]);

  const {
    intl: { language, languageIsSet },
  } = useSelector((state: RootState) => state);

  useEffect(() => {
    if (language === Language.English && !languageIsSet) {
      dispatch(setLanguage(Language.Swedish));
    }
  }, []);

  const {
    singleInvoiceApiStatus,
    singleInvoice,
    singleInvoicePDF,
    singleInvoiceAttachment,
  } = useSelector((state: RootState) => state.factoring);

  const [paymentMethodPage, setPaymentMethodPage] = useState<number>(
    PaymentMethodPageEnum.None
  );

  const onBackButtonClick = () => {
    dispatch(
      getDebtorInvoices(
        singleInvoice.debtorOrgNumber ??
          singleInvoice.debtor.identificationNumber,
        1,
        10,
        InvoiceTabValue.OPEN
      )
    );
    history.push({
      pathname: '/invoices',
    });
  };
  const onCancelButton = () => {
    setPaymentMethodPage(PaymentMethodPageEnum.None);
  };

  const onPayButtonClick = () => {
    setPaymentMethodPage(PaymentMethodPageEnum.PayInInstalmentsPage);
  };
  const openCustomerSupportPage = () => {
    history.push({
      pathname: '/customer-support',
    });
  };

  const onSinglePaymentMethodClick = (page: number) => {
    setPaymentMethodPage(page);
  };

  const renderContent = () => {
    if (
      singleInvoiceApiStatus === ApiStatus.Failed ||
      (!singleInvoice?.statuses?.main &&
        !Object.keys(InvoiceStatuses).includes(
          singleInvoice?.statuses?.main.toLowerCase()
        )) ||
      singleInvoice?.statuses?.main === InvoiceStatuses.pendingAuthorization
    ) {
      return <NotConnected onBackButtonClick={onBackButtonClick} />;
    }
    if (paymentMethodPage === PaymentMethodPageEnum.PaymentMethods) {
      return (
        <PaymentMethodMainContainer>
          <PaymentMethodsHeader onCancelButton={onCancelButton} />
          <PaymentMethodBodyContainer>
            <PaymentMethodHeaderContainer>
              <Typography
                fontWeight={FontWeights.Thick}
                lineHeight={1.8}
                fontSize={1.2}
                color="#444444"
              >
                <StepCircle green />
                <StepCircle />
                <StepCircle />
                <StepCircle />
                <br />
                {translate('Invoices.PaymentMethod')}
              </Typography>
            </PaymentMethodHeaderContainer>
            <SinglePaymentMethodContainer
              onClick={() =>
                onSinglePaymentMethodClick(
                  PaymentMethodPageEnum.PayInInstalmentsPage
                )
              }
            >
              <SinglePaymentMethod
                header="Pay in instalments"
                text="Qred sends you an invoice over 2-4 instalments."
                popular
              />
            </SinglePaymentMethodContainer>
            <SinglePaymentMethod
              header="Delay payment"
              text="Qred sends you an invoice due in either 30, 60 or 90 days."
            />
            <SinglePaymentMethod
              header="Pay on invoice due date"
              text="Qred sends you an invoice due on the original invoice due date."
            />
          </PaymentMethodBodyContainer>
        </PaymentMethodMainContainer>
      );
    }
    if (paymentMethodPage === PaymentMethodPageEnum.PayInInstalmentsPage) {
      return (
        <PayInInstalments
          onCancelButton={() =>
            setPaymentMethodPage(PaymentMethodPageEnum.None)
          }
        />
      );
    }
    if (paymentMethodPage === PaymentMethodPageEnum.Support) {
      return (
        <CustomerSupport
          onCancelButton={() =>
            setPaymentMethodPage(PaymentMethodPageEnum.None)
          }
        />
      );
    }
    return (
      <MainContainer>
        <InvoicePageHeader
          languagePicker
          backButtonLabel="Invoices.Invoices"
          onBackButton={onBackButtonClick}
          customerService={isDesktop}
          onCustomerService={openCustomerSupportPage}
        />
        <BodyLeftContainer style={{ alignItems: 'left' }}>
          <InvoicePageBody
            singleInvoice={singleInvoice}
            singleInvoicePDF={singleInvoicePDF}
            singleInvoiceAttachment={singleInvoiceAttachment}
            onPayButton={onPayButtonClick}
          />
          {!isDesktop && (
            <Circle onClick={openCustomerSupportPage}>
              <HeadsetIcon>
                <StyledIcon
                  color="#2FD67A"
                  icon={SvgSrc.Headset}
                  style={{
                    minWidth: '30px',
                    minHeight: '30px',
                    verticalAlign: 'middle',
                  }}
                />
              </HeadsetIcon>
            </Circle>
          )}
        </BodyLeftContainer>
      </MainContainer>
    );
  };

  return singleInvoiceApiStatus === ApiStatus.Started ? (
    <MainContainer style={{ height: '100vh' }}>
      <InvoiceLoaderHolder>
        <SpinnerCircular color={theme.colors.secondary} />
      </InvoiceLoaderHolder>
    </MainContainer>
  ) : (
    renderContent()
  );
};

export default Invoice;
